import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Shield, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { PHONES, CONTACT } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';
import { Helmet } from 'react-helmet-async';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const KEYWORDS_COVERED = [
  'Kocaeli lastikçi', 'En yakın lastikçi Kocaeli', 'Kocaeli mobil lastikçi',
  'İzmit lastikçi', 'Gebze lastikçi', 'Darıca lastikçi', 'Körfez lastikçi',
  '7/24 lastikçi Kocaeli', 'Acil lastikçi', 'Oto lastikçi Kocaeli',
  'Lastikçi ara Kocaeli', 'Gece lastikçi', 'Lastik tamircisi Kocaeli',
];

export default function KocaeliLastikci() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Kocaeli'de en yakın lastikçi nerede?", "acceptedAnswer": { "@type": "Answer", "text": "7/24 Lastik olarak Kocaeli'nin tüm ilçelerine mobil lastikçi hizmeti veriyoruz. Sabit bir adrese gitmenize gerek yok — mobil aracımızla bulunduğunuz konuma geliyoruz." } },
      { "@type": "Question", "name": "Kocaeli'de gece açık lastikçi var mı?", "acceptedAnswer": { "@type": "Answer", "text": "Evet, 7/24 Lastik olarak gece gündüz 365 gün kesintisiz hizmet veriyoruz. Gece 3'te bile arayın, ekibimiz yola çıkar." } },
      { "@type": "Question", "name": "Kocaeli'de mobil lastikçi hizmeti ne kadar?", "acceptedAnswer": { "@type": "Answer", "text": "Ücret yapılacak işleme göre değişir. Basit tamir ile komple değişim farklı fiyatlandırılır. Arayın, durumunuza göre net fiyat verelim." } },
      { "@type": "Question", "name": "Kocaeli lastikçi hangi ilçelere gelir?", "acceptedAnswer": { "@type": "Answer", "text": "İzmit, Gebze, Darıca, Körfez, Gölcük, Derince, Çayırova, Kartepe, Başiskele, Karamürsel, Kandıra, Dilovası ve Hereke dahil tüm Kocaeli geneline hizmet veriyoruz." } },
      { "@type": "Question", "name": "Kocaeli'de lastikçi ne kadar sürede gelir?", "acceptedAnswer": { "@type": "Answer", "text": "Ortalama 30 dakikada bulunduğunuz konuma ulaşıyoruz. Merkezi ilçelerde (İzmit, Gebze, Derince) bu süre 15-20 dakikaya kadar düşebilir." } },
    ],
  };

  return (
    <main data-testid="kocaeli-lastikci-page" style={{ background: 'var(--bg)' }}>
      <SEOHead
        path="/kocaeli-lastikci"
        title="Kocaeli Lastikçi | En Yakın Lastikçi 7/24 Mobil Lastik Servisi"
        description="Kocaeli lastikçi arıyorsanız doğru yerdesiniz. En yakın lastikçi olarak İzmit, Gebze, Körfez ve tüm ilçelere 7/24 mobil lastik tamiri ve değişimi hizmeti. Hemen arayın: 0534 968 7611"
      />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: 'Kocaeli Lastikçi' },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', position: 'relative', overflow: 'hidden', paddingTop: 86 }}>
        <div style={{
          position: 'absolute', top: '20%', right: '5%',
          width: 500, height: 500,
          background: 'radial-gradient(ellipse, rgba(229,51,51,.06) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'var(--border)' }} />

        <div className="wrap" style={{ position: 'relative', zIndex: 1, padding: '3.5rem 1.5rem 3.5rem' }}>
          <nav data-testid="lastikci-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem', color: 'var(--text2)', marginBottom: '1.25rem' }}>
            <Link to="/" style={{ transition: 'color .2s' }}>Anasayfa</Link>
            <span style={{ color: 'var(--muted)', fontSize: '.65rem' }}>/</span>
            <span style={{ color: 'var(--red)' }}>Kocaeli Lastikçi</span>
          </nav>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem' }}>
            <LiveBadge text="Ekipler Aktif — Hemen Ara" />
          </div>

          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(2rem, 5.5vw, 3.5rem)', color: 'var(--text)',
            lineHeight: 1.05, letterSpacing: '-.04em', marginBottom: '.5rem',
          }}>
            Kocaeli Lastikçi
          </h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.4rem, 4vw, 2.5rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1.25rem',
          }}>
            En Yakın Lastikçi 7/24
          </p>
          <p style={{
            fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)',
            lineHeight: 1.72, maxWidth: 580, marginBottom: '2rem',
          }}>
            <strong style={{ color: 'var(--text)' }}>Kocaeli lastikçi</strong> arıyorsanız doğru adrestesiniz.
            7/24 Lastik olarak Kocaeli'nin tüm ilçelerine <strong style={{ color: 'var(--text)' }}>mobil lastikçi</strong> hizmeti sunuyoruz.
            Servis aramak yerine bizi arayın — biz size gelelim.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', marginBottom: '2rem' }}>
            <a href={PHONES.main_tel} data-testid="lastikci-hero-call" className="btn btn-primary btn-xl ring num" style={{ gap: '.625rem' }}>
              <Phone size={17} /> Hemen Ara — {PHONES.main}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="lastikci-hero-whatsapp" data-gtm-action="whatsapp_click" className="btn btn-green" style={{ gap: '.5rem', padding: '.875rem 1.5rem', borderRadius: 'var(--rl)', fontSize: '.9375rem', fontWeight: 700 }}>
              {WA_ICON} WhatsApp ile Konum Gönder
            </a>
          </div>
        </div>
      </section>

      {/* TRUST METRICS */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }} className="lastikci-trust-grid">
            {[
              { icon: Zap, value: '30 dk', label: 'Ortalama Müdahale', color: 'var(--red)' },
              { icon: Shield, value: '24 Ay', label: 'Garanti', color: 'var(--green)' },
              { icon: MapPin, value: '13+', label: 'İlçe Kapsama', color: 'var(--blue)' },
              { icon: Clock, value: '7/24', label: 'Kesintisiz', color: '#ff9500' },
            ].map(({ icon: Icon, value, label, color }, i) => (
              <div key={label} style={{
                padding: '1.75rem 1rem', borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.875rem',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: `${color}18`, border: `1px solid ${color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0,
                }}>
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1.25rem', color: 'var(--text)', lineHeight: 1, letterSpacing: '-.03em' }}>{value}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginTop: '.15rem' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ maxWidth: 780, fontSize: '.9rem', color: 'var(--text2)', lineHeight: 1.85 }} className="lastikci-content">
            <h2>Kocaeli'de En Yakın Lastikçi — 7/24 Mobil Servis</h2>
            <p>
              <strong>Kocaeli lastikçi</strong> arayışınızda 7/24 Lastik olarak size en hızlı ve en profesyonel çözümü sunuyoruz. İster gece yarısı olsun, ister bayram sabahı — Kocaeli'nin her noktasına <strong>mobil lastikçi</strong> ekibimiz ile ulaşıyoruz.
            </p>

            <h2>Neden Servise Gitmeyin?</h2>
            <p>
              Geleneksel lastikçiler sabit bir adreste sizi bekler. Oysa siz yolda kaldığınızda, o adrese gitmeniz gerekir. <strong>Mobil lastikçi</strong> hizmetimiz ile bu sorunu ortadan kaldırıyoruz. Bizi arayın, biz yanınıza gelelim. <strong>Lastik tamiri</strong>, <strong>lastik değişimi</strong>, <strong>sibop tamiri</strong> — tümünü yerinde yapıyoruz.
            </p>

            <h2>Hangi İlçelere Lastikçi Gelir?</h2>
            <p>Kocaeli genelinde tüm ilçelere mobil lastikçi hizmeti veriyoruz:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', margin: '1rem 0 1.5rem' }}>
              {DISTRICT_PAGES.map(d => (
                <Link key={d.slug} to={`/${d.slug}`} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                  background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 100, padding: '.35rem .85rem', fontSize: '.8rem', color: 'var(--text2)',
                  transition: 'border-color .2s, color .2s', textDecoration: 'none',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                >
                  <MapPin size={12} /> {d.displayName}
                </Link>
              ))}
            </div>

            <h2>Kocaeli Lastikçi SSS</h2>
            {[
              { q: 'Kocaeli\'de en yakın lastikçi nerede?', a: '7/24 Lastik olarak Kocaeli\'nin tüm ilçelerine mobil lastikçi hizmeti veriyoruz. Sabit bir adrese gitmenize gerek yok — mobil aracımızla bulunduğunuz konuma geliyoruz.' },
              { q: 'Kocaeli\'de gece açık lastikçi var mı?', a: 'Evet, gece gündüz 365 gün kesintisiz hizmet veriyoruz. Gece 3\'te bile arayın, ekibimiz yola çıkar.' },
              { q: 'Kocaeli\'de mobil lastikçi hizmeti ne kadar?', a: 'Ücret yapılacak işleme göre değişir. Arayın, durumunuza göre net fiyat verelim.' },
              { q: 'Kocaeli lastikçi hangi ilçelere gelir?', a: 'İzmit, Gebze, Darıca, Körfez, Gölcük, Derince, Çayırova, Kartepe, Başiskele, Karamürsel, Kandıra, Dilovası ve Hereke dahil tüm Kocaeli geneline.' },
              { q: 'Kocaeli\'de lastikçi ne kadar sürede gelir?', a: 'Ortalama 30 dakikada bulunduğunuz konuma ulaşıyoruz. Merkezi ilçelerde bu süre 15-20 dakikaya kadar düşebilir.' },
            ].map((faq, i) => (
              <div key={faq.q.substring(0, 30)} style={{ marginBottom: '1.25rem', padding: '1rem', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--r)' }}>
                <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.9rem', color: 'var(--text)', marginBottom: '.3rem' }}>
                  {faq.q}
                </h3>
                <p style={{ margin: 0, fontSize: '.85rem' }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA headline="Kocaeli'nin En Yakın Lastikçisi." sub="Hemen Arayın." />

      <style>{`
        .lastikci-content h2 { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: clamp(1.2rem, 3vw, 1.6rem); color: var(--text); margin: 2rem 0 .75rem; line-height: 1.2; }
        .lastikci-content strong { color: var(--text); font-weight: 600; }
        @media(max-width:768px) { .lastikci-trust-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media(max-width:480px) { .lastikci-trust-grid { grid-template-columns: 1fr !important; } .lastikci-trust-grid > div { border-right: none !important; border-bottom: 1px solid var(--border); } .lastikci-trust-grid > div:last-child { border-bottom: none; } }
      `}</style>
    </main>
  );
}
